import React, { createContext, useEffect, useState, useCallback } from "react";
import { createTheme } from "@mui/material";
import { themeType } from "../constants/theme-type";
import { localStorageItemNames } from "../constants";

export const ThemeContext = createContext();

const getInitialThemeType = () => {
  const savedTheme = localStorage.getItem(localStorageItemNames.theme);
  return savedTheme === themeType.dark ? themeType.dark : themeType.light;
};

export const createThemes = (typeOfTheme) => {
  const isDark = typeOfTheme === themeType.dark;

  return createTheme({
    palette: {
      mode: typeOfTheme,
      primary: {
        main: isDark ? "#0860d3" : "#0860D3",
        dark: isDark ? "#0555aa" : "#1555AB",
        light: isDark ? "#7dd3fc" : "#208DE9",
        contrastText: "#ffffff",
      },
      secondary: {
        main: isDark ? "#8B5CF6" : "#78C2F8",
        dark: isDark ? "#6D28D9" : "#A2CDF0",
        light: isDark ? "#A78BFA" : "#5FA4E7",
        contrastText: "#ffffff",
      },
      background: {
        default: isDark ? "#020617" : "#F2F7FB",
        paper: isDark ? "#07101F" : "#FFFFFF",
      },
      text: {
        primary: isDark ? "#F8FAFC" : "#020617",
        secondary: isDark ? "#94A3B8" : "#9B9FA2",
      },
      info: {
        main: isDark ? "#7DD3FC" : "#5FA4E7",
      },
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: "'Outfit', 'Inter', 'Roboto', 'Helvetica', sans-serif",
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? "#020617" : "#F2F7FB",
            color: isDark ? "#F8FAFC" : "#020617",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 10,
            boxShadow: "none",
            transition: "all 200ms ease",
            fontWeight: 600,
          },
          contained: {
            backgroundColor: isDark ? "#0555aa" : "#0860D3",
            "&:hover": {
              backgroundColor: isDark ? "#0860d3" : "#208DE9",
            },
            "&:focus-visible": {
              outline: "2px solid #A2CDF0",
              outlineOffset: 2,
            },
          },
          outlined: {
            borderColor: isDark ? "rgba(56,189,248,0.5)" : "#A2CDF0",
            color: isDark ? "#0860d3" : "#0860D3",
            "&:hover": {
              borderColor: isDark ? "#7DD3FC" : "#208DE9",
              backgroundColor: isDark ? "rgba(56,189,248,0.1)" : "rgba(32, 141, 233, 0.06)",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            boxShadow: "none",
            border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
            backgroundColor: isDark ? "#07101F" : "#FFFFFF",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: "none",
            border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
            backgroundColor: isDark ? "#07101F" : "#FFFFFF",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? "#07101F" : "#FFFFFF",
            color: isDark ? "#F8FAFC" : "#020617",
            borderRadius: 16,
            border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.08)",
            boxShadow: isDark ? "0 24px 48px rgba(0,0,0,0.6)" : "0 24px 48px rgba(0, 0, 0, 0.12)",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            color: isDark ? "#0860d3" : "#0860D3",
            fontWeight: 700,
            borderBottom: isDark ? "1px solid rgba(56, 189, 248, 0.15)" : "1px solid rgba(160, 198, 232, 0.35)",
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            color: isDark ? "#F8FAFC" : "#020617",
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.06)",
            padding: "16px 24px",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
            transition: "all 0.2s ease",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? "#0860d3" : "#0860D3",
              borderWidth: "2px",
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? "#07101F" : "#FFFFFF",
            color: isDark ? "#F8FAFC" : "#020617",
            borderRadius: 12,
            border: isDark ? "1px solid rgba(56, 189, 248, 0.15)" : "1px solid rgba(160, 198, 232, 0.35)",
            boxShadow: isDark ? "0 12px 30px rgba(0,0,0,0.45)" : "0 12px 30px rgba(2, 6, 23, 0.08)",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: "2px 6px",
            color: isDark ? "#E2E8F0" : undefined,
            "&:hover": {
              backgroundColor: isDark ? "rgba(56, 189, 248, 0.15)" : "rgba(32, 141, 233, 0.08)",
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: "outlined",
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#FFFFFF",
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? "#0860d3" : "#208DE9",
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#07101F" : "#FFFFFF",
            color: isDark ? "#F8FAFC" : "#020617",
            boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(2, 6, 23, 0.05)",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? "#07101F" : "#FFFFFF",
            color: isDark ? "#F8FAFC" : "#020617",
          },
        },
      },
    },
  });
};

export const ThemeProvider = ({ children }) => {
  const initialThemeType = getInitialThemeType();
  const [currentThemeType, setCurrentThemeType] = useState(initialThemeType);
  const [currentTheme, setCurrentTheme] = useState(createThemes(initialThemeType));

  useEffect(() => {
    localStorage.setItem(localStorageItemNames.theme, currentThemeType);
    document.body.classList.remove("theme-light", "theme-dark");
    document.body.classList.add(`theme-${currentThemeType}`);
  }, [currentThemeType]);

  const onChangeThemeType = useCallback((typeOfTheme) => {
    const theme = createThemes(typeOfTheme);
    setCurrentThemeType(typeOfTheme);
    setCurrentTheme(theme);
  }, []);

  const contextValue = {
    currentTheme,
    currentThemeType,
    changeTheme: onChangeThemeType,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
